/**
 * Landing Page Component
 * 
 * Main marketing page for Zakyam AI Trader.
 * Public page (no authentication required).
 */

import React from "react";
import { PublicLayout } from "../components/public/PublicLayout";
import { CryptoTicker } from "../components/ticker/CryptoTicker";
import "./landing-page.css";

export function LandingPage(): JSX.Element {
  return (
    <PublicLayout>
      <div className="landing-page">
        <CryptoTicker />

        {/* Risk Banner */}
        <div className="landing-risk-banner" role="note">
          <span className="landing-risk-banner__text">
            Trading involves substantial risk of loss. Past performance does not guarantee future results.
          </span>
        </div>

        {/* Hero Section */}
        <section className="landing-hero">
          <div className="landing-hero__container">
            <h1 className="landing-hero__headline">
              AI Trading Signals. Automated Execution. Built for Safety.
            </h1>
            <p className="landing-hero__subheadline">
              Zakyam AI Trader generates trade opportunities using AI and executes them on your Bybit account with safety controls, monitoring, and clear reporting—without complexity.
            </p>
            <p className="landing-hero__no-promise">
              We do not promise profits. Any results shown reflect historical backtesting, not a guarantee of future returns.
            </p>
            <div className="landing-hero__ctas">
              <a
                href="/pricing"
                className="landing-hero__cta landing-hero__cta--primary"
                aria-label="View pricing and get started"
              >
                Get started
              </a>
              <a
                href="/pricing"
                className="landing-hero__cta landing-hero__cta--secondary"
                aria-label="View pricing plans"
              >
                View pricing
              </a>
            </div>
            <p className="landing-hero__trust-line">
              Bybit only • Non-custodial • Encrypted API keys • Trade-only keys recommended • Risk controls included
            </p>
            
            {/* UI Mock Panel */}
            <div className="landing-hero__mock-panel">
              <div className="landing-mock-card">
                <div className="landing-mock-card__header">
                  <span className="landing-mock-card__badge">Signal Detected</span>
                </div>
                <div className="landing-mock-card__content">
                  <div className="landing-mock-card__symbol">BTCUSDT</div>
                  <div className="landing-mock-card__side">Long</div>
                  <div className="landing-mock-card__confidence">Example signal (illustrative)</div>
                </div>
              </div>
              <div className="landing-mock-card__arrow" aria-hidden="true">→</div>
              <div className="landing-mock-card">
                <div className="landing-mock-card__header">
                  <span className="landing-mock-card__badge landing-mock-card__badge--success">Admin Executed</span>
                </div>
                <div className="landing-mock-card__content">
                  <div className="landing-mock-card__text">Order accepted</div>
                  <div className="landing-mock-card__text">Snapshot recorded</div>
                </div>
              </div>
              <div className="landing-mock-card__arrow" aria-hidden="true">→</div>
              <div className="landing-mock-card">
                <div className="landing-mock-card__header">
                  <span className="landing-mock-card__badge landing-mock-card__badge--info">Replicated Execution</span>
                </div>
                <div className="landing-mock-card__content">
                  <div className="landing-mock-card__text">Users executed with per-user sizing</div>
                  <div className="landing-mock-card__text">Monitoring active</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof Strip */}
        <section className="landing-social-proof">
          <div className="landing-social-proof__container">
            <div className="landing-social-proof__item">Non-custodial by design</div>
            <div className="landing-social-proof__item">Trade-only API permissions</div>
            <div className="landing-social-proof__item">Encrypted credentials</div>
            <div className="landing-social-proof__item">Audit-ready logs</div>
          </div>
        </section>

        {/* How It Works */}
        <section className="landing-how-it-works">
          <div className="landing-how-it-works__container">
            <h2 className="landing-section__title">How it works</h2>
            <div className="landing-steps">
              <div className="landing-step">
                <div className="landing-step__number">1</div>
                <h3 className="landing-step__title">AI Signal Detection</h3>
                <p className="landing-step__description">
                  The system monitors the market and identifies setups based on technical signals and real-time conditions.
                </p>
              </div>
              <div className="landing-step">
                <div className="landing-step__number">2</div>
                <h3 className="landing-step__title">Safety & Control Layer</h3>
                <p className="landing-step__description">
                  Decisions pass through safety checks and execution rules designed to reduce risk and avoid accidental over-exposure.
                </p>
              </div>
              <div className="landing-step">
                <div className="landing-step__number">3</div>
                <h3 className="landing-step__title">Execution on Your Account</h3>
                <p className="landing-step__description">
                  Trades are executed on your Bybit account with sizing adapted to your balance and monitored until exit.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach */}
        <section className="landing-approach">
          <div className="landing-approach__container">
            <h2 className="landing-section__title">Our approach: many small, repeated trades</h2>
            <p className="landing-approach__intro">
              Rather than chasing a few large bets, the system is designed around a high frequency of small trades.
              We do not promise, target, or guarantee any specific return, daily or otherwise. This approach is
              grounded in our own historical testing, not in any guarantee of what you will earn.
            </p>

            <div className="landing-approach__grid">
              <div className="landing-approach__card">
                <h3 className="landing-approach__card-title">Historical backtest, not a promise</h3>
                <p className="landing-approach__card-text">
                  Our signals were evaluated over approximately two years of historical backtesting, including
                  losing trades. These are historical results—not a target, not a guarantee, and past results do
                  not predict future results.
                </p>
              </div>
              <div className="landing-approach__card">
                <h3 className="landing-approach__card-title">Small wins, compounded</h3>
                <p className="landing-approach__card-text">
                  Many small trades can, in theory, compound over time. Compounding works in both directions: it can
                  multiply gains, and it can also multiply losses. We never present compounding as a certainty, and
                  we do not set or promise any daily return objective.
                </p>
              </div>
              <div className="landing-approach__card">
                <h3 className="landing-approach__card-title">No guaranteed daily return</h3>
                <p className="landing-approach__card-text">
                  Market conditions change, and any given day can end negative. There is no promised daily, weekly,
                  or monthly return, and we set no daily return target of any kind.
                </p>
              </div>
            </div>

            <p className="landing-approach__disclaimer">
              Backtested results do not include live slippage, liquidity constraints, or exchange fees, and may overstate
              actual live results. No representation is made that any account will achieve results similar to those shown.
            </p>
          </div>
        </section>

        {/* Features Grid */}
        <section className="landing-features">
          <div className="landing-features__container">
            <h2 className="landing-section__title">Why Zakyam</h2>
            <div className="landing-features__grid">
              <div className="landing-feature-card">
                <h3 className="landing-feature-card__title">Non-custodial by design</h3>
                <p className="landing-feature-card__description">
                  Your funds stay on your exchange. You control deposits and withdrawals.
                </p>
              </div>
              <div className="landing-feature-card">
                <h3 className="landing-feature-card__title">Secure key handling</h3>
                <p className="landing-feature-card__description">
                  API secrets are encrypted at rest. Sessions are protected with secure cookies.
                </p>
              </div>
              <div className="landing-feature-card">
                <h3 className="landing-feature-card__title">Monitoring & safe exits</h3>
                <p className="landing-feature-card__description">
                  Positions are monitored and can close via target exits or emergency protection.
                </p>
              </div>
              <div className="landing-feature-card">
                <h3 className="landing-feature-card__title">Transparent billing & history</h3>
                <p className="landing-feature-card__description">
                  Subscription status, renewal date, and payment history are visible inside your profile.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Security & Trust */}
        <section className="landing-security">
          <div className="landing-security__container">
            <h2 className="landing-section__title">Security-first:</h2>
            <div className="landing-security__content">
              <ul className="landing-security__list">
                <li className="landing-security__item">
                  Trade-only API keys recommended (withdrawals disabled)
                </li>
                <li className="landing-security__item">
                  Encrypted secrets + secure sessions
                </li>
                <li className="landing-security__item">
                  Rate limiting + CSRF protection
                </li>
                <li className="landing-security__item">
                  Clear audit trails for billing actions
                </li>
              </ul>
              <p className="landing-security__note">
                Trading involves risk. Past performance is not a guarantee of future results.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="landing-pricing-teaser">
          <div className="landing-pricing-teaser__container">
            <h2 className="landing-section__title">Ready to get started?</h2>
            <p className="landing-pricing-teaser__subtitle">
              Connect your Bybit account and activate your subscription in minutes.
            </p>
            <div className="landing-pricing-teaser__ctas">
              <a
                href="/pricing"
                className="landing-pricing-teaser__cta landing-pricing-teaser__cta--primary"
                aria-label="View pricing and get started"
              >
                Get started
              </a>
              <a
                href="/pricing"
                className="landing-pricing-teaser__cta landing-pricing-teaser__cta--secondary"
                aria-label="View pricing"
              >
                Pricing
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="landing-faq">
          <div className="landing-faq__container">
            <h2 className="landing-section__title">Frequently Asked Questions</h2>
            <div className="landing-faq__list">
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">Is this copy trading?</h3>
                <p className="landing-faq-item__answer">
                  Yes. The system replicates a master account's execution to subscriber accounts via their own
                  API keys. It is non-custodial: your funds stay on your exchange, and you can disable automated
                  trading or revoke your API key at any time.
                </p>
              </div>
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">Do you have access to my funds?</h3>
                <p className="landing-faq-item__answer">
                  No. We are non-custodial. Funds remain on your exchange account.
                </p>
              </div>
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">Can you withdraw from my exchange?</h3>
                <p className="landing-faq-item__answer">
                  No. We require trade-only API permissions with withdrawals disabled.
                </p>
              </div>
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">How are positions sized for my account?</h3>
                <p className="landing-faq-item__answer">
                  Each subscriber account sizes positions based on its own available balance and system allocation rules.
                </p>
              </div>
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">What happens if my API key fails or balance is low?</h3>
                <p className="landing-faq-item__answer">
                  Your execution is skipped safely and other users continue. You'll see a clear error reason.
                </p>
              </div>
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">How do exits work?</h3>
                <p className="landing-faq-item__answer">
                  The system monitors open positions and triggers exits consistently when exit conditions are met.
                </p>
              </div>
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">Do you guarantee profit?</h3>
                <p className="landing-faq-item__answer">
                  No. Trading carries risk.
                </p>
              </div>
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">Do you hold my funds?</h3>
                <p className="landing-faq-item__answer">
                  No. Funds stay on your exchange.
                </p>
              </div>
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">Can I cancel?</h3>
                <p className="landing-faq-item__answer">
                  You can cancel renewal anytime; access continues until period end.
                </p>
              </div>
              <div className="landing-faq-item">
                <h3 className="landing-faq-item__question">What exchanges are supported?</h3>
                <p className="landing-faq-item__answer">
                  Bybit is supported now. More exchanges may be added later.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}

export default LandingPage;

